import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import {
  Eye, EyeOff, Mail, Lock, ArrowRight, ShieldCheck,
  KeyRound, ArrowLeft, CheckCircle2, RefreshCw,
} from 'lucide-react';

// ── Shared design tokens ──────────────────────────────────────────────────────

const S = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#061a06',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Outfit', sans-serif",
  },

  bg: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: `
      radial-gradient(ellipse 140% 70% at 50% -10%, rgba(34,197,34,0.60) 0%, transparent 55%),
      radial-gradient(ellipse 60% 40% at 100% 70%, rgba(22,163,22,0.12) 0%, transparent 50%),
      radial-gradient(ellipse 50% 30% at 0% 60%,  rgba(22,163,22,0.08) 0%, transparent 50%)
    `,
  },

  bgGrid: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(34,197,34,0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(34,197,34,0.06) 1px, transparent 1px)
    `,
    backgroundSize: '32px 32px',
    pointerEvents: 'none',
  },

  bgOrb: {
    position: 'absolute',
    width: 500,
    height: 500,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(34,197,34,0.28) 0%, transparent 68%)',
    top: -220,
    left: '50%',
    transform: 'translateX(-50%)',
    filter: 'blur(28px)',
    pointerEvents: 'none',
  },

  top: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '36px 24px 0',
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
    marginBottom: 28,
  },

  logoWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    animation: 'lpDrop 0.6s cubic-bezier(.34,1.56,.64,1) both',
  },

  logoGlow: {
    display: 'none',
  },

  logo: {
    position: 'relative',
    zIndex: 1,
    background: '#ffffff',
    borderRadius: 18,
    padding: '12px 22px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.20)',
  },

  title: {
    fontSize: 30,
    fontWeight: 900,
    color: '#fff',
    letterSpacing: '-0.6px',
    marginBottom: 5,
    lineHeight: 1.1,
    animation: 'lpUp 0.5s ease 0.1s both',
  },

  sub: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.40)',
    fontWeight: 500,
    animation: 'lpUp 0.5s ease 0.15s both',
  },

  waveWrap: {
    position: 'relative',
    marginTop: 0,
    zIndex: 2,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },

  waveSvg: { display: 'block', width: '100%', marginBottom: -2 },

  sheet: {
    background: '#fff',
    flex: 1,
    padding: '24px 26px 32px',
    paddingBottom: 'max(32px, env(safe-area-inset-bottom, 32px))',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    animation: 'lpUp 0.45s ease 0.2s both',
  },

  handle: {
    width: 34,
    height: 4,
    background: '#dde8dd',
    borderRadius: 2,
    margin: '0 auto 20px',
    flexShrink: 0,
  },

  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 13px',
    background: '#f0fdf0',
    border: '1px solid #bbdebb',
    borderRadius: 9,
    fontSize: 12,
    fontWeight: 700,
    color: '#14a014',
    marginBottom: 20,
    alignSelf: 'center',
    flexShrink: 0,
    letterSpacing: '0.2px',
  },

  form: { display: 'flex', flexDirection: 'column', gap: 16 },

  field: { display: 'flex', flexDirection: 'column', gap: 6 },

  label: {
    fontSize: 11,
    fontWeight: 700,
    color: '#2e3d2e',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
  },

  inpBox: {
    display: 'flex',
    alignItems: 'center',
    background: '#f5faf5',
    border: '1.5px solid #cce8cc',
    borderRadius: 14,
    overflow: 'hidden',
    transition: 'border-color .18s, box-shadow .18s, background .18s',
  },

  inpBoxFocus: {
    borderColor: '#1fc51f',
    boxShadow: '0 0 0 3.5px rgba(34,197,34,0.12)',
    background: '#fff',
  },

  inpIcon: {
    width: 46,
    height: 52,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#7ab87a',
    flexShrink: 0,
    borderRight: '1.5px solid #dff0df',
  },

  inp: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontFamily: "'Outfit', sans-serif",
    fontSize: 15,
    color: '#0d1f0d',
    padding: '0 14px',
    height: 52,
    WebkitAppearance: 'none',
  },

  eyeBtn: {
    width: 46,
    height: 52,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#7aaa7a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },

  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#fff5f5',
    border: '1.5px solid #fecaca',
    borderRadius: 12,
    padding: '12px 14px',
    color: '#dc2626',
    fontSize: 13,
    fontWeight: 500,
  },

  errorDot: {
    width: 20,
    height: 20,
    minWidth: 20,
    background: '#dc2626',
    color: '#fff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 800,
  },

  successBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    background: '#f0fdf0',
    border: '1.5px solid #86efac',
    borderRadius: 12,
    padding: '12px 14px',
    color: '#15803d',
    fontSize: 13,
    fontWeight: 500,
  },

  btn: {
    width: '100%',
    background: 'linear-gradient(135deg, #2ed02e, #178a17)',
    color: '#fff',
    border: 'none',
    borderRadius: 14,
    fontSize: 16,
    fontWeight: 800,
    letterSpacing: '0.3px',
    padding: '0 20px',
    height: 54,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    boxShadow: '0 8px 24px rgba(34,197,34,0.40), 0 2px 6px rgba(0,0,0,0.10)',
    transition: 'all .2s',
    fontFamily: "'Outfit', sans-serif",
    WebkitTapHighlightColor: 'transparent',
    marginTop: 4,
    flexShrink: 0,
  },

  btnSecondary: {
    width: '100%',
    background: 'transparent',
    color: '#178a17',
    border: '1.5px solid #cce8cc',
    borderRadius: 14,
    fontSize: 14,
    fontWeight: 700,
    padding: '0 20px',
    height: 46,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    transition: 'all .2s',
    fontFamily: "'Outfit', sans-serif",
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0,
  },

  spin: {
    width: 18,
    height: 18,
    border: '2.5px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'lpSpin .65s linear infinite',
    flexShrink: 0,
  },

  forgotLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: '#14a014',
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'underline',
    textDecorationColor: 'rgba(20,160,20,0.35)',
    textUnderlineOffset: 3,
    fontFamily: "'Outfit', sans-serif",
    alignSelf: 'flex-end',
    WebkitTapHighlightColor: 'transparent',
  },

  hintText: {
    fontSize: 13,
    color: '#5a7a5a',
    lineHeight: 1.55,
    margin: '0 0 4px',
  },

  footer: {
    textAlign: 'center',
    fontSize: 11,
    color: '#aac4aa',
    paddingTop: 18,
    marginTop: 'auto',
    flexShrink: 0,
    letterSpacing: '0.2px',
  },
};

// ── Keyframe CSS ──────────────────────────────────────────────────────────────

const KEYFRAMES = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;700;800;900&display=swap');

@keyframes lpDrop{from{opacity:0;transform:translateY(-22px) scale(0.88)}to{opacity:1;transform:translateY(0) scale(1)}}
@keyframes lpPulse{0%,100%{opacity:0.45;transform:scale(1)}50%{opacity:1;transform:scale(1.10)}}
@keyframes lpUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
@keyframes lpSpin{to{transform:rotate(360deg)}}

@media(min-width:600px){
  .lp-root-inner{align-items:center;justify-content:center;min-height:100vh;height:auto !important;}
  .lp-wave-wrap-inner{flex:0 0 auto !important;margin-top:0 !important;width:100%;max-width:460px;}
  .lp-sheet-inner{
    border-radius:26px !important;
    flex:0 0 auto !important;
    height:auto !important;
    overflow:visible !important;
    box-shadow:0 32px 90px rgba(0,0,0,0.60),0 8px 24px rgba(0,0,0,0.25),0 0 0 1px rgba(34,197,34,0.13),inset 0 3px 0 rgba(34,197,34,0.50) !important;
  }
  .lp-wave-inner{display:none !important;}
  .lp-handle{display:none !important;}
  .lp-top-inner{padding-top:0 !important;padding-bottom:24px;}
}
`;

// ── Reusable sub-components ───────────────────────────────────────────────────

function InputField({ id, label, type, placeholder, value, onChange, icon, autoComplete, rightSlot, focusState, onFocus, onBlur }) {
  return (
    <div style={S.field}>
      <label htmlFor={id} style={S.label}>{label}</label>
      <div style={{ ...S.inpBox, ...(focusState ? S.inpBoxFocus : {}) }}>
        <div style={{ ...S.inpIcon, color: focusState ? '#1fc51f' : '#7ab87a' }}>
          {icon}
        </div>
        <input
          id={id}
          style={S.inp}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          required
          autoComplete={autoComplete}
        />
        {rightSlot}
      </div>
    </div>
  );
}

function ErrorAlert({ message }) {
  if (!message) return null;
  return (
    <div style={S.errorBox}>
      <span style={S.errorDot}>!</span>
      {message}
    </div>
  );
}

function PrimaryButton({ loading, children, disabled, ...rest }) {
  return (
    <button
      type="submit"
      style={S.btn}
      disabled={loading || disabled}
      onMouseEnter={e => {
        if (!loading) {
          e.currentTarget.style.boxShadow = '0 14px 34px rgba(34,197,34,0.55), 0 2px 6px rgba(0,0,0,0.12)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(34,197,34,0.40), 0 2px 6px rgba(0,0,0,0.10)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      {...rest}
    >
      {loading ? <><span style={S.spin} />Please wait…</> : children}
    </button>
  );
}

// ── View: Login ───────────────────────────────────────────────────────────────

function LoginView({ onForgotPassword }) {
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [showPass,  setShowPass]  = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);
  const [passFocus,  setPassFocus]  = useState(false);

  const { login, loading, error } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await login(email, password);
    if (ok) navigate('/dashboard');
  };

  return (
    <>
      <div style={S.badge}>
        <ShieldCheck size={14} />
        <span>Secure Admin Login</span>
      </div>

      <form onSubmit={handleSubmit} style={S.form}>
        <InputField
          id="login-email"
          label="Email Address"
          type="email"
          placeholder="admin@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          icon={<Mail size={17} strokeWidth={1.8} />}
          autoComplete="email"
          focusState={emailFocus}
          onFocus={() => setEmailFocus(true)}
          onBlur={() => setEmailFocus(false)}
        />

        <InputField
          id="login-password"
          label="Password"
          type={showPass ? 'text' : 'password'}
          placeholder="Enter your password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          icon={<Lock size={17} strokeWidth={1.8} />}
          autoComplete="current-password"
          focusState={passFocus}
          onFocus={() => setPassFocus(true)}
          onBlur={() => setPassFocus(false)}
          rightSlot={
            <button
              type="button"
              style={S.eyeBtn}
              onClick={() => setShowPass(v => !v)}
              aria-label={showPass ? 'Hide password' : 'Show password'}
            >
              {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          }
        />


        <ErrorAlert message={error} />

        <PrimaryButton loading={loading}>
          Sign In <ArrowRight size={18} />
        </PrimaryButton>
      </form>
    </>
  );
}

// ── View: Forgot Password — Step 1 (enter email) ──────────────────────────────

function ForgotPasswordRequestView({ onBack, onTokenReceived }) {
  const [email,      setEmail]      = useState('');
  const [emailFocus, setEmailFocus] = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.adminForgotPasswordRequest(email.trim().toLowerCase());
      const { resetToken } = res.data;
      onTokenReceived(resetToken, email.trim().toLowerCase());
    } catch (err) {
      const msg = err?.response?.data?.error || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={S.badge}>
        <KeyRound size={14} />
        <span>Reset Admin Password</span>
      </div>

      <p style={S.hintText}>
        Enter your admin email address. A one-time reset token will be generated
        for you to set a new password.
      </p>

      <form onSubmit={handleSubmit} style={{ ...S.form, marginTop: 8 }}>
        <InputField
          id="fp-email"
          label="Admin Email Address"
          type="email"
          placeholder="admin@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          icon={<Mail size={17} strokeWidth={1.8} />}
          autoComplete="email"
          focusState={emailFocus}
          onFocus={() => setEmailFocus(true)}
          onBlur={() => setEmailFocus(false)}
        />

        <ErrorAlert message={error} />

        <PrimaryButton loading={loading}>
          Get Reset Token <ArrowRight size={18} />
        </PrimaryButton>

        <button type="button" style={S.btnSecondary} onClick={onBack}>
          <ArrowLeft size={15} /> Back to Login
        </button>
      </form>
    </>
  );
}

// ── View: Forgot Password — Step 2 (enter token + new password) ───────────────

function ForgotPasswordResetView({ resetToken, adminEmail, onBack, onSuccess }) {
  const [token,         setToken]         = useState(resetToken || '');
  const [newPassword,   setNewPassword]   = useState('');
  const [confirmPass,   setConfirmPass]   = useState('');
  const [showNew,       setShowNew]       = useState(false);
  const [showConfirm,   setShowConfirm]   = useState(false);
  const [newPassFocus,  setNewPassFocus]  = useState(false);
  const [confPassFocus, setConfPassFocus] = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPass) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.adminForgotPasswordReset(token, newPassword, confirmPass);
      onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={S.badge}>
        <KeyRound size={14} />
        <span>Set New Password</span>
      </div>

      {adminEmail && (
        <p style={S.hintText}>
          Setting new password for <strong>{adminEmail}</strong>.
          The reset token expires in 2 minutes.
        </p>
      )}

      <form onSubmit={handleSubmit} style={{ ...S.form, marginTop: 8 }}>
        {/* Token field — hidden/pre-filled when token comes from step 1 */}
        {!resetToken && (
          <div style={S.field}>
            <label htmlFor="fp-token" style={S.label}>Reset Token</label>
            <div style={S.inpBox}>
              <div style={S.inpIcon}><RefreshCw size={15} strokeWidth={1.8} /></div>
              <input
                id="fp-token"
                style={S.inp}
                type="text"
                placeholder="Paste your reset token"
                value={token}
                onChange={e => setToken(e.target.value)}
                required
                autoComplete="off"
              />
            </div>
          </div>
        )}

        <InputField
          id="fp-new-pass"
          label="New Password"
          type={showNew ? 'text' : 'password'}
          placeholder="Min. 8 characters"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          icon={<Lock size={17} strokeWidth={1.8} />}
          autoComplete="new-password"
          focusState={newPassFocus}
          onFocus={() => setNewPassFocus(true)}
          onBlur={() => setNewPassFocus(false)}
          rightSlot={
            <button
              type="button"
              style={S.eyeBtn}
              onClick={() => setShowNew(v => !v)}
              aria-label={showNew ? 'Hide password' : 'Show password'}
            >
              {showNew ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          }
        />

        <InputField
          id="fp-confirm-pass"
          label="Confirm New Password"
          type={showConfirm ? 'text' : 'password'}
          placeholder="Re-enter new password"
          value={confirmPass}
          onChange={e => setConfirmPass(e.target.value)}
          icon={<Lock size={17} strokeWidth={1.8} />}
          autoComplete="new-password"
          focusState={confPassFocus}
          onFocus={() => setConfPassFocus(true)}
          onBlur={() => setConfPassFocus(false)}
          rightSlot={
            <button
              type="button"
              style={S.eyeBtn}
              onClick={() => setShowConfirm(v => !v)}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          }
        />

        <ErrorAlert message={error} />

        <PrimaryButton loading={loading}>
          Reset Password <ArrowRight size={18} />
        </PrimaryButton>

        <button type="button" style={S.btnSecondary} onClick={onBack}>
          <ArrowLeft size={15} /> Back
        </button>
      </form>
    </>
  );
}

// ── View: Success confirmation ────────────────────────────────────────────────

function SuccessView({ onBackToLogin }) {
  return (
    <>
      <div style={{ ...S.badge, background: '#f0fdf0', color: '#15803d', border: '1px solid #86efac' }}>
        <CheckCircle2 size={14} />
        <span>Password Reset</span>
      </div>

      <div style={S.successBox}>
        <CheckCircle2 size={20} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Your admin password has been reset successfully. You can now sign in
          with your new password.
        </span>
      </div>

      <div style={{ marginTop: 20 }}>
        <button
          type="button"
          style={S.btn}
          onClick={onBackToLogin}
          onMouseEnter={e => {
            e.currentTarget.style.boxShadow = '0 14px 34px rgba(34,197,34,0.55), 0 2px 6px rgba(0,0,0,0.12)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(34,197,34,0.40), 0 2px 6px rgba(0,0,0,0.10)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Back to Login <ArrowRight size={18} />
        </button>
      </div>
    </>
  );
}

// ── Root component ────────────────────────────────────────────────────────────

export default function Login() {
  const [view,       setView]       = useState('login');
  const [resetToken, setResetToken] = useState('');
  const [adminEmail, setAdminEmail] = useState('');

  const handleTokenReceived = (token, email) => {
    setResetToken(token);
    setAdminEmail(email);
    setView('reset');
  };

  const renderView = () => {
    switch (view) {
      case 'request':
        return (
          <ForgotPasswordRequestView
            onBack={() => setView('login')}
            onTokenReceived={handleTokenReceived}
          />
        );
      case 'reset':
        return (
          <ForgotPasswordResetView
            resetToken={resetToken}
            adminEmail={adminEmail}
            onBack={() => setView('request')}
            onSuccess={() => setView('success')}
          />
        );
      case 'success':
        return <SuccessView onBackToLogin={() => setView('login')} />;
      default:
        return <LoginView onForgotPassword={() => setView('request')} />;
    }
  };

  const titles = {
    login:   { heading: 'Welcome Back',      sub: 'HerGhar Central' },
    request: { heading: 'Forgot Password?',  sub: 'Verify your admin account'  },
    reset:   { heading: 'New Password',      sub: 'Secure your admin account'  },
    success: { heading: 'All Done!',         sub: 'HerGhar Central' },
  };

  const { heading, sub } = titles[view] || titles.login;

  return (
    <>
      <style>{KEYFRAMES}</style>

      <div style={S.root} className="lp-root-inner">
        <div style={S.bg} />
        <div style={S.bgGrid} />
        <div style={S.bgOrb} />

        {/* Branding */}
        <div style={S.top} className="lp-top-inner">
            <div style={S.logoWrap}>
            <div style={S.logoGlow} />
            <div style={S.logo}>
              <img
                src="/hergharlogo.jpeg"
                alt="HerGhar"
                style={{ height: 56, width: 'auto', display: 'block' }}
              />
            </div>
          </div>
          <h1 style={S.title}>{heading}</h1>
          <p style={S.sub}>{sub}</p>
        </div>

        {/* Sheet */}
        <div style={S.waveWrap} className="lp-wave-wrap-inner">
          <svg
            className="lp-wave-inner"
            style={S.waveSvg}
            viewBox="0 0 390 72"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path
              d="M0,72 L0,38 C30,18 70,8 110,20 C150,32 180,52 220,48
                 C260,44 300,24 340,18 C365,14 380,20 390,28 L390,72 Z"
              fill="#ffffff"
            />
          </svg>

          <div style={S.sheet} className="lp-sheet-inner">
            <div style={S.handle} className="lp-handle" />

            {renderView()}

            <p style={S.footer}>HerGhar © 2026</p>
          </div>
        </div>
      </div>
    </>
  );
}